import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  icon?: React.ReactNode;
  iconColor?: string;
  amberArrow?: boolean;
}

export default function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  className = '',
  icon,
  iconColor = 'text-gray-400',
  amberArrow = true,
}: CustomSelectProps) {
  const [aberto, setAberto] = useState(false);
  const [flash, setFlash] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const handleClickFora = (e: MouseEvent) => {
      // Delay to allow button click to register first
      setTimeout(() => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          setAberto(false);
        }
      }, 10);
    };
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setAberto(false);
    if (e.key === 'Enter' || e.key === ' ') setAberto(!aberto);
    if (aberto && e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = options.findIndex((o) => o.value === value);
      const next = options[Math.min(idx + 1, options.length - 1)];
      if (next) onChange(next.value);
    }
    if (aberto && e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = options.findIndex((o) => o.value === value);
      const prev = options[Math.max(idx - 1, 0)];
      if (prev) onChange(prev.value);
    }
  };

  const getDropdownStyle = (): React.CSSProperties => {
    if (!buttonRef.current) return { display: 'none', top: 0, left: 0, position: 'absolute' as const };

    const rect = buttonRef.current.getBoundingClientRect();
    const DROPDOWN_HEIGHT = Math.min(220, options.length * 44);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top: number;
    // Prefer opening below if there's enough space
    if (spaceBelow >= DROPDOWN_HEIGHT || spaceBelow >= spaceAbove) {
      top = rect.bottom + 4;
    } else {
      // Flip up
      top = rect.top - DROPDOWN_HEIGHT - 4;
      // If it would go above viewport top, just position at top with scroll
      if (top < 8) top = 8;
    }

    // Ensure left stays within viewport
    const left = Math.min(Math.max(rect.left, 8), window.innerWidth - rect.width - 8);

    return {
      position: 'absolute' as const,
      top,
      left,
      minWidth: rect.width,
      maxHeight: DROPDOWN_HEIGHT,
      overflowY: 'auto' as const,
      zIndex: 100010,
    };
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setAberto(!aberto)}
        onKeyDown={handleKeyDown}
        className={`
          w-full appearance-none flex items-center gap-2 rounded-xl pl-4 pr-10 py-2.5 text-sm font-semibold
          bg-white border transition-all duration-200 cursor-pointer text-left
          focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400
          ${aberto
            ? 'border-amber-400 shadow-md bg-white'
            : 'border-gray-200 hover:border-amber-300 hover:shadow-md'
          }
          ${flash ? 'bg-emerald-50 border-emerald-400' : ''}
          ${selected ? 'text-gray-700' : 'text-gray-400'}
        `}
      >
        {icon && (
          <span className={`flex-shrink-0 ${iconColor}`}>{icon}</span>
        )}
        <span className="flex-1 truncate">{flash ? `✓ ${selected?.label || placeholder}` : (selected ? selected.label : placeholder)}</span>
        <span className={`flex-shrink-0 transition-transform duration-200 ${aberto ? 'rotate-180' : ''}`}>
          {amberArrow ? (
            <i className="ri-arrow-down-s-line text-amber-500 text-lg" />
          ) : (
            <i className="ri-arrow-down-s-line text-gray-400 text-lg" />
          )}
        </span>
      </button>

      {aberto && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (e.target === e.currentTarget) setAberto(false);
          }}
        >
          <div
            style={{
              ...getDropdownStyle(),
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ paddingTop: '4px', paddingBottom: '4px' }}>
              {options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    onChange(opt.value);
                    setFlash(true);
                    setTimeout(() => {
                      setAberto(false);
                      setTimeout(() => setFlash(false), 300);
                    }, 100);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    fontSize: '14px',
                    textAlign: 'left' as const,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: opt.value === value ? '#fef3c7' : 'white',
                    color: opt.value === value ? '#92400e' : '#374151',
                    fontWeight: opt.value === value ? '600' : '400',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (opt.value !== value) (e.target as HTMLElement).style.background = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    if (opt.value !== value) (e.target as HTMLElement).style.background = 'white';
                  }}
                >
                  {opt.value === value && (
                    <i className="ri-check-line text-amber-500" style={{ fontSize: '14px', flexShrink: 0 }} />
                  )}
                  {opt.value !== value && <span style={{ width: '14px', flexShrink: 0 }} />}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
